import React, { createContext, useContext, useState, useCallback } from 'react';
import { SUPPORTED_TOKENS } from '@/config/tokens';

interface SwapState {
  fromToken: string;
  toToken: string;
  fromAmount: string;
  toAmount: string;
  isLoading: boolean;
  error: string | null;
  slippage: number;
}

interface SwapContextType {
  swapState: SwapState;
  setFromToken: (token: string) => void;
  setToToken: (token: string) => void;
  setFromAmount: (amount: string) => void;
  setToAmount: (amount: string) => void;
  swapTokens: () => void;
  getQuote: () => Promise<void>;
  setSlippage: (slippage: number) => void;
  resetSwap: () => void;
}

const SwapContext = createContext<SwapContextType | undefined>(undefined);

export const useSwap = () => {
  const context = useContext(SwapContext);
  if (!context) {
    throw new Error('useSwap must be used within a SwapProvider');
  }
  return context;
};

export const SwapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [swapState, setSwapState] = useState<SwapState>({
    fromToken: 'ETH',
    toToken: 'FSPY',
    fromAmount: '',
    toAmount: '',
    isLoading: false,
    error: null,
    slippage: 0.5
  });

  const setFromToken = useCallback((token: string) => {
    setSwapState(prev => ({ ...prev, fromToken: token, fromAmount: '', toAmount: '' }));
  }, []);

  const setToToken = useCallback((token: string) => {
    setSwapState(prev => ({ ...prev, toToken: token, fromAmount: '', toAmount: '' }));
  }, []);

  const setFromAmount = useCallback((amount: string) => {
    setSwapState(prev => ({ ...prev, fromAmount: amount }));
  }, []);

  const setToAmount = useCallback((amount: string) => {
    setSwapState(prev => ({ ...prev, toAmount: amount }));
  }, []);

  const setSlippage = useCallback((slippage: number) => {
    setSwapState(prev => ({ ...prev, slippage }));
  }, []);

  const getQuote = useCallback(async () => {
    if (!swapState.fromAmount || parseFloat(swapState.fromAmount) <= 0) return;
    
    setSwapState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Mock quote calculation - replace with actual DEX API calls
      const mockRate = 1337; // 1 ETH = 1337 FSPY (example)
      const estimatedOutput = (parseFloat(swapState.fromAmount) * mockRate).toFixed(6);
      
      setTimeout(() => {
        setSwapState(prev => ({ 
          ...prev, 
          toAmount: estimatedOutput,
          isLoading: false 
        }));
      }, 1000);
    } catch (error) {
      setSwapState(prev => ({ 
        ...prev, 
        error: 'Failed to get quote',
        isLoading: false 
      }));
    }
  }, [swapState.fromAmount, swapState.fromToken, swapState.toToken]);

  const swapTokens = useCallback(() => {
    setSwapState(prev => ({
      ...prev,
      fromToken: prev.toToken,
      toToken: prev.fromToken,
      fromAmount: prev.toAmount,
      toAmount: prev.fromAmount
    }));
  }, []);

  const resetSwap = useCallback(() => {
    setSwapState({
      fromToken: 'ETH',
      toToken: 'FSPY',
      fromAmount: '',
      toAmount: '',
      isLoading: false,
      error: null,
      slippage: 0.5
    });
  }, []);

  const value = {
    swapState,
    setFromToken,
    setToToken,
    setFromAmount,
    setToAmount,
    swapTokens,
    getQuote,
    setSlippage,
    resetSwap
  };

  return (
    <SwapContext.Provider value={value}>
      {children}
    </SwapContext.Provider>
  );
};